import { memo, useState } from "react";

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/system/Box";

import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";

import { Finding, FindingState, IgnorableStates } from "stores/FindingStore";

import BulkIgnoreDialog from "components/Ignore/Dialogs/BulkIgnoreDialog";
import BulkUnignoreDialog from "components/Ignore/Dialogs/BulkUnignoreDialog";

import SeverityDialog from "./SeverityDialog";
import StateDialog from "./StateDialog";

// -----------------------------------------------------------------------------

type ActionType = "ignore" | "unignore" | "acknowledge";

const OrganizationFindingBulkActions = memo(
  function OrganizationFindingBulkActions({
    findings,
    resetSelectedFindings,
  }: OrganizationFindingBulkActionsProps): JSX.Element | null {
    const [showStateDialog, setShowStateDialog] = useState(false);
    const [showIgnoreDialog, setShowIgnoreDialog] = useState(false);
    const [showUnignoreDialog, setShowUnignoreDialog] = useState(false);
    const [showSeverityDialog, setShowSeverityDialog] = useState(false);
    const [actionType, setActionType] = useState<ActionType | undefined>(
      undefined
    );
    const [actionableFindings, setActionableFindings] = useState<
      Finding[] | undefined
    >(undefined);

    // Suppressed is inside of IgnorableStates for ease of use elsewhere but in
    // this case we want to be explicit between ignore and unignore
    const ignorableFindings = findings.filter(
      (finding) =>
        finding.state !== FindingState.Suppressed &&
        IgnorableStates.includes(finding.state)
    );
    const unignoreableFindings = findings.filter(
      (finding) => finding.state === FindingState.Suppressed
    );
    const acknowledgableFindings = findings.filter(
      (finding) => finding.state === FindingState.New
    );

    const openIgnore = () => {
      setActionType("ignore");
      setActionableFindings(ignorableFindings);
      setShowIgnoreDialog(true);
    };

    const openUnignore = () => {
      setActionType("unignore");
      setActionableFindings(unignoreableFindings);
      setShowUnignoreDialog(true);
    };

    const openAcknowledge = () => {
      setActionType("acknowledge");
      setActionableFindings(acknowledgableFindings);
      setShowStateDialog(true);
    };

    const openSeverity = () => {
      setShowSeverityDialog(true);
    };

    const closeDialog = () => {
      setActionType(undefined);
      setShowStateDialog(false);
      setShowSeverityDialog(false);
    };

    return (
      <>
        <Stack
          spacing={1}
          direction="row"
          sx={{ mt: 2, alignItems: "center", flex: 3 }}
        >
          <Box
            sx={{
              cursor: "pointer",
              display: "flex",
            }}
            onClick={() => resetSelectedFindings(findings[0], false, false)}
          >
            <IndeterminateCheckBoxIcon sx={{ color: "primary.main" }} />
          </Box>

          {acknowledgableFindings.length > 0 && (
            <Button variant="contained" onClick={openAcknowledge}>
              Acknowledge
            </Button>
          )}
          {ignorableFindings.length > 0 && (
            <Button variant="outlined" onClick={openIgnore}>
              Ignore
            </Button>
          )}
          {unignoreableFindings.length > 0 && (
            <Button variant="contained" onClick={openUnignore}>
              Unignore
            </Button>
          )}
          <Button variant="outlined" onClick={openSeverity}>
            Edit severity
          </Button>
        </Stack>
        {showSeverityDialog && (
          <SeverityDialog findings={findings} onClose={closeDialog} />
        )}
        {showStateDialog && (
          <StateDialog
            findings={actionableFindings}
            type={actionType}
            onClose={closeDialog}
          />
        )}

        {showIgnoreDialog && (
          <BulkIgnoreDialog
            findings={findings}
            onClose={() => setShowIgnoreDialog(false)}
          />
        )}
        {showUnignoreDialog && (
          <BulkUnignoreDialog
            findings={findings}
            onClose={() => setShowUnignoreDialog(false)}
          />
        )}
      </>
    );
  }
);

export default OrganizationFindingBulkActions;

// -----------------------------------------------------------------------------

interface OrganizationFindingBulkActionsProps {
  findings: Finding[];
  resetSelectedFindings: (
    finding: Finding,
    appendToList: boolean,
    removeFromList: boolean
  ) => void;
}
